import React from 'react';
import { FileText, ExternalLink, Download, ClipboardList, Lock } from 'lucide-react';

/**
 * ProgramViewer
 * Renders a premium training or nutrition program: PDF embed + tracker button + month archive.
 *
 * Props:
 *  - title: section heading (e.g. "Strength & Speed Training")
 *  - accentColor: CSS color for accents
 *  - icon: lucide icon component
 *  - programs: array of programs (already sorted newest-first)
 *  - selectedId: currently selected program id
 *  - onSelect: (id) => void
 *  - loading: boolean
 */
const ProgramViewer = ({ title, accentColor, icon: Icon, programs, selectedId, onSelect, loading, emptyMessage }) => {
  const selected = (programs || []).find(p => p.id === selectedId) || programs?.[0];

  return (
    <div className="network-card" style={{ padding: '32px' }} data-testid={`program-section-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {Icon && <Icon size={32} style={{ color: accentColor }} />}
        <h2 className="heading-2" style={{ margin: 0 }}>{title}</h2>
      </div>

      {loading && (
        <p style={{ color: 'var(--text-light)' }}>Loading programs…</p>
      )}

      {!loading && (!programs || programs.length === 0) && (
        <div style={{
          padding: '24px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px dashed var(--border-medium)',
          borderRadius: '12px',
          color: 'var(--text-light)',
          textAlign: 'center'
        }}>
          {emptyMessage || 'No programs published yet. Check back soon!'}
        </div>
      )}

      {!loading && selected && (
        <>
          {/* Month archive selector */}
          {programs.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }} data-testid="program-archive-selector">
              {programs.map(p => {
                const isActive = p.id === selected.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => onSelect(p.id)}
                    data-testid={`program-tab-${p.id}`}
                    style={{
                      padding: '8px 14px',
                      background: isActive ? accentColor : 'rgba(255,255,255,0.08)',
                      color: isActive ? 'black' : 'var(--text-light)',
                      border: 'none',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.85rem',
                      transition: 'all 0.15s'
                    }}
                  >
                    {p.month} {p.year}
                  </button>
                );
              })}
            </div>
          )}

          {/* Program header card */}
          <div style={{
            background: `linear-gradient(135deg, ${accentColor}22, ${accentColor}08)`,
            border: `1px solid ${accentColor}55`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: accentColor, fontWeight: 700, marginBottom: '4px' }}>
                  {selected.month} {selected.year}
                </div>
                <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)' }}>{selected.title}</h3>
                {selected.description && (
                  <p style={{ margin: 0, color: 'var(--text-light)', fontSize: '0.9rem', lineHeight: 1.55 }}>
                    {selected.description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {selected.tracker_url && (
                  <a
                    href={selected.tracker_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="program-tracker-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '10px 16px',
                      background: 'var(--brand-primary)', color: 'black',
                      borderRadius: '8px', textDecoration: 'none', fontWeight: 700,
                      fontSize: '0.9rem'
                    }}
                  >
                    <ClipboardList size={16} /> Open My Tracker
                  </a>
                )}
                <a
                  href={selected.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="program-download-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 16px',
                    background: 'rgba(255,255,255,0.1)', color: 'var(--text-primary)',
                    borderRadius: '8px', textDecoration: 'none', fontWeight: 600,
                    fontSize: '0.9rem', border: '1px solid var(--border-medium)'
                  }}
                >
                  <Download size={16} /> Download PDF
                </a>
              </div>
            </div>
            {selected.tracker_url && (
              <p style={{
                margin: '14px 0 0',
                fontSize: '0.8rem',
                color: 'var(--text-light)',
                background: 'rgba(0,0,0,0.2)',
                padding: '10px 14px',
                borderRadius: '8px',
                borderLeft: `3px solid ${accentColor}`
              }}>
                <strong style={{ color: 'var(--text-primary)' }}>Tip:</strong> Clicking "Open My Tracker" will prompt Google Sheets to make a personal copy in your Drive — log your sets, reps, and progress privately.
              </p>
            )}
          </div>

          {/* PDF embed */}
          <div style={{ position: 'relative', width: '100%', height: '78vh', minHeight: '600px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border-medium)' }}>
            <object
              data={`${selected.pdf_url}#toolbar=1&navpanes=0&zoom=page-width`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ display: 'block' }}
              data-testid="program-pdf-embed"
              aria-label={`${selected.title} PDF document`}
            >
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <FileText size={42} style={{ color: 'var(--text-light)', marginBottom: '12px' }} />
                <p style={{ color: 'var(--text-light)' }}>
                  Your browser cannot display this PDF inline.
                </p>
                <a
                  href={selected.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: accentColor, fontWeight: 600, marginTop: '8px' }}
                >
                  <ExternalLink size={16} /> Open in new tab
                </a>
              </div>
            </object>
          </div>
        </>
      )}
    </div>
  );
};

export const PremiumLockedCard = ({ title, icon: Icon, accentColor, onUpgradeClick }) => (
  <div className="network-card" style={{ padding: '32px', textAlign: 'center' }}>
    <Lock size={42} style={{ color: accentColor, marginBottom: '12px' }} />
    <h2 className="heading-2" style={{ margin: '0 0 8px' }}>{title}</h2>
    <p style={{ color: 'var(--text-light)', maxWidth: '480px', margin: '0 auto 18px' }}>
      Unlock our coaches' monthly training & nutrition programs, plus your personal progress tracker. Premium members get access to the entire archive.
    </p>
    <button onClick={onUpgradeClick} className="btn-primary" data-testid="program-upgrade-btn">
      Upgrade to Premium
    </button>
  </div>
);

export default ProgramViewer;
